import React, { useEffect, useState } from 'react'

import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import CircularProgress from '@mui/material/CircularProgress'
import FormControlLabel from '@mui/material/FormControlLabel'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TextField from '@mui/material/TextField'

import { apiCorrectPrepaidServices, apiGetPrepaidClubs, apiGetPrepaidServices,
         isApiError } from './Api'
import { ServiceRow } from './Types'
import { CourierRun } from './Courier'
import ConfirmDialog from './ConfirmDialog'
import SelectInput from './SelectInput'
import formatCurrency from './Formatting'

const ore = (value: number) => formatCurrency(value / 100)
// Amounts inside a sentence carry their unit; the table column has a header instead.
const kr = (value: number) => `${ore(value)} kr`
// A club to work on: the id to fetch with, and the name to say out loud. Whoever links here
// hands over both, since it knows them and this view would have to fetch a whole club list to
// learn the second one.
export type FocusClub = { id: number, name: string }
type Club = FocusClub
type Service = { id: number, name: string }

type Change = { row: ServiceRow, quantity: number }

/**
 * What a correction will do, in mechanism and in money: what the new invoice credits,
 * what it re-bills, and what the club is left owing.
 *
 * Out here rather than inside the component because it is the text the whole feature
 * turns on, shown twice — under the checkbox that decides it, and in the dialog that asks
 * you to agree to it — so choosing and consenting cannot be told two different stories.
 */
export function describeEffect(changes: Change[], billDifference: boolean): string {
    const survives = changes.some(c => c.quantity > 0)
    // Billing the difference credits the whole old row; leaving the invoice alone credits only
    // what is re-ordered, which cancels the new invoice out.
    const credited = changes.reduce(
        (sum, c) => sum + (billDifference ? c.row.quantity : c.quantity) * c.row.unit_price_ore,
        0)
    const rebilled = changes.reduce((sum, c) => sum + c.quantity * c.row.unit_price_ore, 0)
    const back = credited - rebilled
    if (!survives && !billDifference)
        return 'Eko tar bort raderna i Eventor. Ingen faktura skapas, så klubben betalar lika '
             + 'mycket som förut.'
    const changesAntal = survives
        ? 'Eko ändrar antalen i Eventor och beställer om det som blir kvar, vilket skapar en '
          + 'ny faktura. '
        : 'Eko tar bort raderna i Eventor och skapar en ny faktura. '
    // A free service — Bussparkering — has nothing to credit either way, and saying so beats
    // "krediterar hela det gamla antalet (0,00 kr)".
    if (credited === 0 && rebilled === 0)
        return changesAntal + 'Raderna kostar ingenting, så fakturan går på 0 kr.'
    if (!billDifference)
        return changesAntal + `Den krediterar bara det som beställs om (${kr(rebilled)}), så `
             + 'den går på 0 kr och klubben betalar lika mycket som förut.'
    // A row priced at 0 kr nets to nothing, and one priced below 0 — a correction somebody
    // else made — nets the other way. Both are said as they are rather than assumed away.
    const settled = back > 0 ? `så klubben får tillbaka ${kr(back)}.`
                  : back < 0 ? `så klubben faktureras ${kr(-back)}.`
                  : 'så fakturan går på 0 kr.'
    return changesAntal + `Den krediterar hela det gamla antalet (${kr(credited)})`
         + (rebilled !== 0 ? ` och fakturerar det nya (${kr(rebilled)})` : '')
         + `, ${settled}`
}

/**
 * Correcting a club's ordered services — the one thing nobody can do in Eventor.
 *
 * Its own view rather than a branch of the reconciliation, because a club that needs correcting is
 * often in perfect balance: ten wind shelters ordered by mistake and ten invoiced agree with each
 * other, and the reconciliation has nothing to say. The trigger is the club's need, which only a
 * person knows about. The reconciliation links here for the clubs it *does* have something to say
 * about.
 *
 * The unit of correction is how many the club should be left with. Reducing keeps the row, and with
 * it the date it was ordered on, which is what its price derives from; removing and re-ordering
 * would price the replacement in whatever deadline window is open now. See
 * docs/eventor-peculiarities.md.
 */
export default function PrepaidServices({ paymentEventId, courier, running, setRunning,
                                          focusClub, onFocused }: {
    paymentEventId: number | null,
    courier: CourierRun,
    running: string | null,
    setRunning: (what: string | null) => void,
    // A club handed over from the reconciliation, opened once and then released so that coming
    // back to this tab later does not silently re-open somebody it was finished with.
    focusClub?: FocusClub | null,
    onFocused?: () => void,
}) {
    const [clubs, setClubs] = useState<Club[] | null>(null)
    const [clubId, setClubId] = useState<number | null>(null)
    const [rows, setRows] = useState<ServiceRow[] | null>(null)
    const [wanted, setWanted] = useState<Record<number, string>>({})
    const [billDifference, setBillDifference] = useState(true)
    const [message, setMessage] = useState<string | null>(null)
    const [failure, setFailure] = useState<string | null>(null)
    const [confirming, setConfirming] = useState(false)
    const [handedName, setHandedName] = useState<string | null>(null)
    // The competition's service menu, as Eventor offers it, and the one picked to carry a
    // credit-only correction. Both live for as long as the club's rows do.
    const [services, setServices] = useState<Service[]>([])
    const [changeService, setChangeService] = useState<string | null>(null)
    // Why this club cannot be corrected, in the server's words — see apiGetPrepaidServices.
    const [refusal, setRefusal] = useState<string | null>(null)
    // Competitions to choose between, when the payment event covers several.
    const [candidates, setCandidates] = useState<number[]>([])

    useEffect(() => { setClubs(null); setClubId(null); setRows(null) }, [paymentEventId])
    useEffect(() => {
        if (focusClub !== null && focusClub !== undefined && focusClub.id !== clubId) {
            loadRows(focusClub.id, focusClub.name)
            onFocused?.()
        }
    })

    const loadClubs = (eventId?: number) => {
        if (paymentEventId === null) return
        setFailure(null)
        void courier.run(async () => {
            setRunning('Klubblista')
            try {
                const answer = await apiGetPrepaidClubs(paymentEventId, eventId)
                setCandidates(answer.candidates)
                setClubs(answer.candidates.length > 0 ? null : answer.clubs)
            } finally { setRunning(null) }
        }).catch(() => {})
    }

    // `named` is how a club handed over from elsewhere is called: the picker's list is not loaded
    // in that case, and the handover is released as soon as it is taken, so the name has to be
    // kept here or the view has nothing to call the club it is showing.
    //
    // `keepFailure` is for the one caller that re-reads *because* something failed. Clearing it
    // there wiped the only durable account of what went wrong, in the same tick it was written --
    // and that is how a correction that stopped half-way came to look like a correction that had
    // simply done nothing.
    const loadRows = (club: number, named: string | null = null, keepFailure = false) => {
        setClubId(club)
        setHandedName(named)
        setRows(null)
        setServices([])
        setChangeService(null)
        setRefusal(null)
        setWanted({})
        setMessage(null)
        if (!keepFailure) setFailure(null)
        if (paymentEventId === null) return
        void courier.run(async () => {
            setRunning('Klubbens tjänster')
            try {
                const answer = await apiGetPrepaidClubs(paymentEventId)
                if (answer.event_id === null) {
                    setCandidates(answer.candidates)
                    return
                }
                const held = await apiGetPrepaidServices(answer.event_id, club)
                setRows(held.rows)
                setServices(held.services)
                setRefusal(held.refusal)
            } finally { setRunning(null) }
        }).catch(() => {})
    }

    // Only a reduction is a correction. Raising a number would order something on the club's
    // behalf, which is a different act and not one to make from here.
    const parsed = (row: ServiceRow): number | null => {
        const text = wanted[row.request_id]
        if (text === undefined || text.trim() === '') return null
        const value = Number(text)
        if (!Number.isInteger(value) || value < 0 || value >= row.quantity) return null
        return value
    }
    // Why a number typed here is not a correction. Anything unusable used to be dropped in
    // silence, which looks exactly like a correction about to happen: the field holds what was
    // typed and the row simply never reaches the summary. Digits are all the field accepts, so
    // what is left to say is about the number itself.
    const rejection = (row: ServiceRow): string | null => {
        const text = wanted[row.request_id]
        if (text === undefined || text.trim() === '') return null
        const value = Number(text)
        if (value === row.quantity) return 'Oförändrat'
        if (value > row.quantity) return 'Bara minskning'
        return null
    }
    const changes = (rows ?? [])
        .map(row => ({ row, quantity: parsed(row) }))
        .filter((c): c is { row: ServiceRow, quantity: number } => c.quantity !== null)
    const clubName = clubs?.find(club => club.id === clubId)?.name ?? handedName
    // Removing everything orders nothing back, so no invoice is raised to write the credit on
    // and one has to be created for the purpose — Eventor will not raise an order with nothing on
    // it, it answers "sparades" and creates nothing. See docs/eventor-peculiarities.md. Asked here,
    // at the only moment it means anything, rather than kept as a setting nobody could fill in
    // without reading Eventor's markup.
    const needsChangeService = changes.length > 0 && !changes.some(c => c.quantity > 0)
                               && billDifference

    const effect = describeEffect(changes, billDifference)
    // Handed a club from elsewhere, there is no list for the picker to show it in — so it shows
    // that one club, rather than standing empty above a table of somebody's services.
    const options = clubs ?? (clubId !== null && handedName !== null
                              ? [{ id: clubId, name: handedName }] : [])

    const apply = () => {
        if (paymentEventId === null || clubId === null) return
        setFailure(null)
        setMessage(null)
        void courier.run(async () => {
            setRunning('Rättning av tjänster')
            try {
                const result = await apiCorrectPrepaidServices(
                    paymentEventId, clubId, billDifference,
                    changes.map(c => ({ request_id: c.row.request_id, quantity: c.quantity })),
                    needsChangeService ? changeService : null)
                setRows(result.rows)
                setWanted({})
                // Where the invoice is, not only that there is one: it is made out to the
                // club but placed by us, so looking for it on their page would not find it.
                setMessage(result.order_reference === null
                    ? 'Klart. Ingen ny faktura behövdes.'
                    : `Klart. Eventor skapade faktura ${result.order_reference} på `
                      + `${kr(result.order_amount_ore)}. Du hittar den på vår egen tävlingssida.`)
            } finally { setRunning(null) }
        }).catch((error: unknown) => {
            // What the server said, kept on the page. It also goes to the error bus, but that is a
            // snackbar with a life of a few seconds, and this is an action people start in Eko and
            // then watch from the Eventor tab. The words matter: a correction can stop between
            // changing the registrations and crediting the invoice, and only the message says
            // which half happened.
            setFailure(isApiError(error) ? error.message : 'Rättningen gick inte igenom.')
            // Re-read so what is shown is what Eventor holds, keeping the message.
            if (clubId !== null) loadRows(clubId, null, true)
        })
    }

    return (
        <div style={{ display: 'flex', flexFlow: 'column nowrap', gap: '0.75rem',
                      width: '100%', maxWidth: '60rem' }}>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                {/* Only once there is something to choose. An empty picker beside the button that
                    fills it says nothing about which to use, and invites the click that does
                    nothing. */}
                {options.length > 0 &&
                    <SelectInput<number, Club, never> label="Klubb" required={false} sx={{ minWidth: 280 }}
                        options={options} valueGetter={c => c.id} nameGetter={c => c.name}
                        value={clubId} onChange={club => { if (club !== null) loadRows(club) }}/>}
                {/* Always offered, never only before the first attempt. A club that entered after
                    the list was read is not in it, and a fetch that came back empty used to take
                    the button with it — leaving no way to try again but reloading Eko. */}
                {candidates.length === 0 &&
                    <Button variant="outlined"
                            disabled={paymentEventId === null || courier.needsClick || running !== null}
                            onClick={() => loadClubs()}>
                        {options.length === 0 ? 'Hämta klubbar' : 'Uppdatera klubblistan'}
                    </Button>}
                {clubs !== null && clubs.length === 0 && running === null &&
                    <span style={{ color: '#b26a00', fontSize: '0.9em' }}>
                        Eventor listade inga klubbar för tävlingen.
                    </span>}
                {running !== null &&
                    <span style={{ display: 'flex', alignItems: 'center', gap: '0.4rem',
                                   color: '#555', fontSize: '0.9em' }}>
                        <CircularProgress size="1em"/> {running} pågår
                    </span>}
            </div>

            {/* Eventor ties a payment event to its competition only from the competition's side,
                so one run as several events is ambiguous until somebody says which. */}
            {candidates.length > 0 &&
                <div>
                    <p>Flera tävlingar delar betalningsomgången. Vilken gäller?</p>
                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                        {candidates.map(id =>
                            <Button key={id} variant="outlined" disabled={running !== null}
                                    onClick={() => { setCandidates([]); loadClubs(id) }}>
                                Tävling {id}
                            </Button>)}
                    </div>
                </div>}

            {failure !== null && <Alert severity="error">{failure}</Alert>}
            {message !== null && <Alert severity="success">{message}</Alert>}

            {refusal !== null && <Alert severity="warning">{refusal}</Alert>}

            {rows !== null && rows.length === 0 && clubId !== null &&
                <div style={{ color: '#555' }}>Klubben har inga beställda tjänster.</div>}

            {rows !== null && rows.length > 0 &&
                <Table size="small">
                    <TableHead><TableRow>
                        <TableCell>Tjänst</TableCell>
                        <TableCell align="right">Antal nu</TableCell>
                        <TableCell align="right">Ska bli</TableCell>
                        <TableCell align="right">Pris</TableCell>
                        <TableCell>Beställd</TableCell>
                        <TableCell>Status</TableCell>
                    </TableRow></TableHead>
                    <TableBody>
                        {rows.map(row =>
                            <TableRow key={row.request_id}>
                                <TableCell>{row.name}</TableCell>
                                <TableCell align="right">{row.quantity}</TableCell>
                                <TableCell align="right">
                                    <TextField size="small" disabled={running !== null}
                                        placeholder={String(row.quantity)}
                                        value={wanted[row.request_id] ?? ''}
                                        error={rejection(row) !== null}
                                        helperText={rejection(row) ?? undefined}
                                        // Digits only, so a letter or a minus sign cannot be typed
                                        // at all rather than being accepted and then disregarded.
                                        onChange={e => setWanted(p => ({
                                            ...p,
                                            [row.request_id]: e.target.value.replace(/\D/g, '') }))}
                                        inputProps={{ inputMode: 'numeric',
                                                      style: { textAlign: 'right', width: '3rem' } }}/>
                                </TableCell>
                                <TableCell align="right">{ore(row.unit_price_ore)}</TableCell>
                                <TableCell>{row.ordered_at}</TableCell>
                                <TableCell>{row.status}</TableCell>
                            </TableRow>)}
                    </TableBody>
                </Table>}

            {changes.length > 0 && <>
                <Box sx={{ fontSize: '0.9em' }}>
                    {changes.map(c =>
                        <div key={c.row.request_id}>
                            {c.row.name}: {c.row.quantity} → {c.quantity}
                            {c.quantity === 0 && ' (raden tas bort)'}
                        </div>)}
                </Box>
                <Box>
                    <FormControlLabel
                        control={<Checkbox checked={billDifference} size="small"
                                           onChange={e => setBillDifference(e.target.checked)}/>}
                        label="Fakturera mellanskillnaden"/>
                    <div style={{ fontSize: '0.9em', color: '#555' }}>{effect}</div>
                </Box>
                <div>
                    <Button variant="contained" color="error"
                            disabled={running !== null || refusal !== null}
                            onClick={() => setConfirming(true)}>
                        Rätta ({changes.length})…
                    </Button>
                </div>
            </>}

            {/* The one destructive write Eko makes into Eventor, and the one place it raises an
                invoice — neither of which can be undone from here. So the click that starts it is
                a click on a dialog that says so, not on a button beside a field just typed in. */}
            <ConfirmDialog
                open={confirming}
                title={`Rätta tjänster för ${clubName ?? 'klubben'}?`}
                confirmLabel="Rätta i Eventor"
                confirmDisabled={needsChangeService && changeService === null}
                message={<>
                    {changes.map(c =>
                        <div key={c.row.request_id}>
                            {c.row.name}: {c.row.quantity} → {c.quantity} st
                            {c.quantity === 0 && ' (raden tas bort)'}
                        </div>)}
                    <p>{effect}</p>
                    {needsChangeService &&
                        <div style={{ marginBottom: '0.5rem' }}>
                            <p>
                                Eventor gör ingen beställning utan innehåll, så Eko beställer en
                                tjänst som kostar 0 kr åt klubben. Det är den som bär
                                krediteringen. Vilken av tävlingens tjänster ska användas?
                            </p>
                            <SelectInput<string, Service, never>
                                label="Tjänst (0 kr)" required={false} sx={{ minWidth: 260 }}
                                options={services} valueGetter={s => s.name} nameGetter={s => s.name}
                                value={changeService} onChange={setChangeService}/>
                        </div>}
                    <p>
                        Fakturan ställs på klubben, men eftersom det är Eko som lägger
                        beställningen hittar du den på vår egen tävlingssida i Eventor, inte på
                        klubbens.
                    </p>
                    <p>Det går inte att ångra från Eko.</p>
                </>}
                onCancel={() => setConfirming(false)}
                onConfirm={() => { setConfirming(false); apply() }}/>
        </div>
    )
}
